/**
 * 权限资源管理页面
 * 左侧权限树 + 右侧详情面板
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card, Tree, Input, Button, Space, Descriptions, Tag, Empty,
  Modal, message, Spin, Row, Col, Tooltip, Drawer, Form, InputNumber,
  List, Result, Badge,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  FolderOutlined, AppstoreOutlined, ApiOutlined,
  ExpandAltOutlined, ShrinkOutlined, ExclamationCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, AimOutlined,
  SearchOutlined, NodeIndexOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { DataNode, TreeProps } from 'antd/es/tree';
import {
  getPermissionList, getPermissionById, deletePermission, getPermissionRoles,
} from '../../api/permission';
import { batchCheckPermissions } from '../../api/permissionEngine';
import type { Permission, PermissionQueryParams, PermissionCheckResult } from '../../api/types';
import { PermissionType, PermissionStatus } from '../../api/types';
import PermissionGuard from '@/modules/shared/components/common/Permission';
import {
  PERM_PERMISSIONS_CREATE, PERM_PERMISSIONS_UPDATE, PERM_PERMISSIONS_DELETE,
} from '@/constants/permissions';
import PermissionForm from './PermissionForm';

const { Search } = Input;
const { confirm } = Modal;

/** Get icon component based on permission type */
const getTypeIcon = (type: PermissionType) => {
  switch (type) {
    case PermissionType.DIRECTORY:
      return <FolderOutlined style={{ color: '#faad14' }} />;
    case PermissionType.MENU:
      return <AppstoreOutlined style={{ color: '#1890ff' }} />;
    case PermissionType.BUTTON:
      return <ApiOutlined style={{ color: '#52c41a' }} />;
    default:
      return <FolderOutlined />;
  }
};

/** Get type label text */
const getTypeLabel = (type: PermissionType) => {
  switch (type) {
    case PermissionType.DIRECTORY:
      return '目录';
    case PermissionType.MENU:
      return '菜单';
    case PermissionType.BUTTON:
      return '按钮';
    default:
      return '未知';
  }
};

/** Get type tag color */
const getTypeColor = (type: PermissionType) => {
  switch (type) {
    case PermissionType.DIRECTORY:
      return 'orange';
    case PermissionType.MENU:
      return 'blue';
    case PermissionType.BUTTON:
      return 'green';
    default:
      return 'default';
  }
};

/** Convert Permission tree to Ant Design Tree DataNode */
const convertToTreeData = (permissions: Permission[]): DataNode[] => {
  return permissions.map((perm) => ({
    key: perm.id,
    title: (
      <Space size={4}>
        {getTypeIcon(perm.menuType)}
        <span>{perm.name}</span>
        {perm.code && (
          <span style={{ color: '#999', fontSize: 12 }}>({perm.code})</span>
        )}
      </Space>
    ),
    icon: getTypeIcon(perm.menuType),
    children: perm.children ? convertToTreeData(perm.children) : undefined,
  }));
};

/** Collect all tree node keys for expand/collapse */
const collectAllKeys = (permissions: Permission[]): string[] => {
  const keys: string[] = [];
  const walk = (list: Permission[]) => {
    for (const perm of list) {
      keys.push(perm.id);
      if (perm.children?.length) {
        walk(perm.children);
      }
    }
  };
  walk(permissions);
  return keys;
};

/** Flatten tree for search matching */
const flattenTree = (permissions: Permission[]): Permission[] => {
  const result: Permission[] = [];
  const walk = (list: Permission[]) => {
    for (const perm of list) {
      result.push(perm);
      if (perm.children?.length) {
        walk(perm.children);
      }
    }
  };
  walk(permissions);
  return result;
};

/** Filter tree by keyword, keeping parent chain */
const filterTree = (permissions: Permission[], keyword: string): Permission[] => {
  if (!keyword.trim()) return permissions;
  const lower = keyword.toLowerCase();

  const filter = (list: Permission[]): Permission[] => {
    return list.reduce<Permission[]>((acc, perm) => {
      const childMatches = perm.children ? filter(perm.children) : [];
      const selfMatches =
        perm.name.toLowerCase().includes(lower) ||
        perm.code.toLowerCase().includes(lower);

      if (selfMatches || childMatches.length > 0) {
        acc.push({
          ...perm,
          children: childMatches.length > 0 ? childMatches : perm.children,
        });
      }
      return acc;
    }, []);
  };

  return filter(permissions);
};

const PermissionResourceList: React.FC = () => {
  const navigate = useNavigate();
  const [permissionTree, setPermissionTree] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  // Drawer state
  const [formVisible, setFormVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [parentIdForNew, setParentIdForNew] = useState<string | undefined>(undefined);

  // Related roles
  const [relatedRoles, setRelatedRoles] = useState<any[]>([]);
  const [relatedRolesLoading, setRelatedRolesLoading] = useState(false);

  // Permission check drawer state
  const [checkVisible, setCheckVisible] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkResults, setCheckResults] = useState<Record<string, PermissionCheckResult> | null>(null);
  const [checkForm] = Form.useForm();

  /** Fetch the full permission tree */
  const fetchPermissionTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPermissionList();
      const tree = Array.isArray(res?.data) ? res.data : [];
      setPermissionTree(tree);
      // Auto expand first level
      const firstLevelKeys = tree.map((p: Permission) => p.id);
      setExpandedKeys(firstLevelKeys);
    } catch (error) {
      console.error('获取权限树失败', error);
      message.error('获取权限列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissionTree();
  }, [fetchPermissionTree]);

  /** Fetch related roles for a permission */
  const fetchRelatedRoles = async (permId: string) => {
    setRelatedRolesLoading(true);
    try {
      const res: any = await getPermissionRoles(permId);
      const data = res?.data;
      const items = Array.isArray(data) ? data : data?.list || [];
      setRelatedRoles(items);
    } catch {
      setRelatedRoles([]);
    } finally {
      setRelatedRolesLoading(false);
    }
  };

  /** Handle tree node selection */
  const handleSelect: TreeProps['onSelect'] = async (selectedKeys) => {
    if (selectedKeys.length === 0) {
      setSelectedPermission(null);
      setRelatedRoles([]);
      return;
    }
    const id = selectedKeys[0] as string;
    setDetailLoading(true);
    try {
      const res = await getPermissionById(id);
      if (res?.data) {
        setSelectedPermission(res.data as Permission);
      }
    } catch (error) {
      console.error('获取权限详情失败', error);
      // Fallback: find from local tree
      const flat = flattenTree(permissionTree);
      const found = flat.find((p) => p.id === id);
      if (found) {
        setSelectedPermission(found);
      }
    } finally {
      setDetailLoading(false);
    }
    // Fetch related roles in parallel
    fetchRelatedRoles(id);
  };

  /** Handle expand/collapse keys */
  const handleExpand: TreeProps['onExpand'] = (keys) => {
    setExpandedKeys(keys);
    setAutoExpandParent(false);
  };

  /** Expand all */
  const handleExpandAll = () => {
    const allKeys = collectAllKeys(permissionTree);
    setExpandedKeys(allKeys);
  };

  /** Collapse all */
  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  /** Search handler */
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    if (value.trim()) {
      // Expand all to show search results
      const allKeys = collectAllKeys(permissionTree);
      setExpandedKeys(allKeys);
      setAutoExpandParent(true);
    }
  };

  /** Open form for new permission */
  const handleAddPermission = () => {
    setEditingPermission(null);
    setParentIdForNew(selectedPermission?.id);
    setFormVisible(true);
  };

  /** Open form for editing */
  const handleEditPermission = () => {
    if (!selectedPermission) return;
    setEditingPermission(selectedPermission);
    setParentIdForNew(undefined);
    setFormVisible(true);
  };

  /** Delete permission */
  const handleDeletePermission = () => {
    if (!selectedPermission) return;

    confirm({
      title: '确定要删除此权限节点吗？',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>权限名称：<strong>{selectedPermission.name}</strong></p>
          <p>权限编码：<strong>{selectedPermission.code}</strong></p>
          {selectedPermission.children && selectedPermission.children.length > 0 && (
            <p style={{ color: '#ff4d4f' }}>
              注意：该节点包含子节点，删除后子节点也将被移除。
            </p>
          )}
        </div>
      ),
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deletePermission(selectedPermission.id);
          message.success('删除成功');
          setSelectedPermission(null);
          fetchPermissionTree();
        } catch (error) {
          console.error('删除权限失败', error);
          message.error('删除失败');
        }
      },
    });
  };

  /** Form submit success callback */
  const handleFormSuccess = () => {
    setFormVisible(false);
    setEditingPermission(null);
    setParentIdForNew(undefined);
    fetchPermissionTree();
    // Refresh detail if editing current node
    if (selectedPermission && editingPermission?.id === selectedPermission.id) {
      getPermissionById(selectedPermission.id).then((res) => {
        if (res?.data) {
          setSelectedPermission(res.data as Permission);
        }
      });
    }
  };

  /** Handle permission batch check */
  const handlePermissionCheck = async (values: { userId: number; permissionCodes: string }) => {
    const codes = values.permissionCodes
      .split(/[,，\n\s]+/)
      .map((c) => c.trim())
      .filter(Boolean);
    if (codes.length === 0) {
      message.warning('请输入至少一个权限编码');
      return;
    }
    setCheckLoading(true);
    setCheckResults(null);
    try {
      const res: any = await batchCheckPermissions({
        userId: values.userId,
        permissionCodes: codes,
      });
      const data = res?.data?.data || res?.data;
      setCheckResults(data?.results || data || {});
    } catch {
      message.error('权限检查失败');
    } finally {
      setCheckLoading(false);
    }
  };

  /** Filtered tree data for rendering */
  const filteredTree = useMemo(() => {
    return filterTree(permissionTree, searchKeyword);
  }, [permissionTree, searchKeyword]);

  const treeData = useMemo(() => {
    return convertToTreeData(filteredTree);
  }, [filteredTree]);

  return (
    <div style={{ height: '100%' }}>
      {/* Top toolbar */}
      <Card
        bordered={false}
        styles={{ body: { padding: '12px 24px' } }}
        style={{ marginBottom: 16 }}
      >
        <Space>
          <PermissionGuard permission={PERM_PERMISSIONS_CREATE}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddPermission}
            >
              新增权限
            </Button>
          </PermissionGuard>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSearchKeyword('');
              setSelectedPermission(null);
              fetchPermissionTree();
            }}
          >
            刷新
          </Button>
          <Button
            icon={<SearchOutlined />}
            onClick={() => {
              setCheckVisible(true);
              setCheckResults(null);
              checkForm.resetFields();
            }}
          >
            权限检查
          </Button>
          <Button
            icon={<NodeIndexOutlined />}
            onClick={() => navigate('/tenant/system/permission-tracing')}
          >
            权限溯源
          </Button>
        </Space>
      </Card>

      {/* Main content: tree + detail */}
      <Row gutter={16} style={{ height: 'calc(100% - 72px)' }}>
        {/* Left: Permission Tree */}
        <Col span={14}>
          <Card
            title="权限树"
            bordered={false}
            style={{ height: '100%' }}
            styles={{ body: { padding: '16px', overflow: 'auto', maxHeight: 'calc(100vh - 240px)' } }}
            extra={
              <Space size="small">
                <Tooltip title="全部展开">
                  <Button
                    type="text"
                    size="small"
                    icon={<ExpandAltOutlined />}
                    onClick={handleExpandAll}
                  />
                </Tooltip>
                <Tooltip title="全部收起">
                  <Button
                    type="text"
                    size="small"
                    icon={<ShrinkOutlined />}
                    onClick={handleCollapseAll}
                  />
                </Tooltip>
              </Space>
            }
          >
            {/* Search */}
            <Search
              placeholder="搜索权限名称或编码"
              allowClear
              onSearch={handleSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  setSearchKeyword('');
                }
              }}
              style={{ marginBottom: 12 }}
            />

            <Spin spinning={loading}>
              {treeData.length > 0 ? (
                <Tree
                  showLine={{ showLeafIcon: false }}
                  blockNode
                  selectedKeys={selectedPermission ? [selectedPermission.id] : []}
                  expandedKeys={expandedKeys}
                  autoExpandParent={autoExpandParent}
                  onSelect={handleSelect}
                  onExpand={handleExpand}
                  treeData={treeData}
                />
              ) : (
                <Empty
                  description={searchKeyword ? '未找到匹配的权限节点' : '暂无权限数据'}
                  style={{ marginTop: 60 }}
                />
              )}
            </Spin>
          </Card>
        </Col>

        {/* Right: Detail Panel */}
        <Col span={10}>
          <Card
            title="权限详情"
            bordered={false}
            style={{ height: '100%' }}
            styles={{ body: { padding: '16px', overflow: 'auto', maxHeight: 'calc(100vh - 240px)' } }}
            extra={
              selectedPermission && (
                <Space size="small">
                  <PermissionGuard permission={PERM_PERMISSIONS_UPDATE}>
                    <Button
                      type="primary"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={handleEditPermission}
                    >
                      编辑
                    </Button>
                  </PermissionGuard>
                  <PermissionGuard permission={PERM_PERMISSIONS_DELETE}>
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={handleDeletePermission}
                    >
                      删除
                    </Button>
                  </PermissionGuard>
                </Space>
              )
            }
          >
            <Spin spinning={detailLoading}>
              {selectedPermission ? (
                <>
                  <Descriptions
                    column={1}
                    bordered
                    size="small"
                    labelStyle={{ width: 120, fontWeight: 500 }}
                  >
                    <Descriptions.Item label="权限名称">
                      {selectedPermission.name}
                    </Descriptions.Item>
                    <Descriptions.Item label="权限编码">
                      <Tag>{selectedPermission.code}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="权限类型">
                      <Tag
                        icon={getTypeIcon(selectedPermission.menuType)}
                        color={getTypeColor(selectedPermission.menuType)}
                      >
                        {getTypeLabel(selectedPermission.menuType)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="上级权限">
                      {selectedPermission.parentName || (selectedPermission.parentId ? selectedPermission.parentId : '无（顶级节点）')}
                    </Descriptions.Item>
                    <Descriptions.Item label="层级">
                      {selectedPermission.level}
                    </Descriptions.Item>
                    <Descriptions.Item label="排序">
                      {selectedPermission.sort ?? selectedPermission.sortNumber ?? 0}
                    </Descriptions.Item>
                    <Descriptions.Item label="状态">
                      <Tag color={selectedPermission.status === PermissionStatus.NORMAL ? 'success' : 'default'}>
                        {selectedPermission.status === PermissionStatus.NORMAL ? '启用' : '禁用'}
                      </Tag>
                    </Descriptions.Item>
                    {selectedPermission.path && (
                      <Descriptions.Item label="路径">
                        <code>{selectedPermission.path}</code>
                      </Descriptions.Item>
                    )}
                    {(selectedPermission as any).method && (
                      <Descriptions.Item label="HTTP 方法">
                        <Tag>{(selectedPermission as any).method}</Tag>
                      </Descriptions.Item>
                    )}
                    {selectedPermission.description && (
                      <Descriptions.Item label="描述">
                        {selectedPermission.description}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="创建时间">
                      {selectedPermission.createdAt}
                    </Descriptions.Item>
                    <Descriptions.Item label="更新时间">
                      {selectedPermission.updatedAt}
                    </Descriptions.Item>
                  </Descriptions>

                  {/* 关联角色 */}
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>
                      关联角色 {relatedRoles.length > 0 && <Tag>{relatedRoles.length}</Tag>}
                    </div>
                    <Spin spinning={relatedRolesLoading}>
                      {relatedRoles.length > 0 ? (
                        <List
                          size="small"
                          bordered
                          dataSource={relatedRoles}
                          renderItem={(role: any) => (
                            <List.Item>
                              <Space>
                                <span>{role.name}</span>
                                <Tag>{role.code}</Tag>
                                <Tag color={role.status === 1 ? 'success' : 'default'}>
                                  {role.status === 1 ? '启用' : '禁用'}
                                </Tag>
                              </Space>
                            </List.Item>
                          )}
                        />
                      ) : (
                        !relatedRolesLoading && (
                          <div style={{ color: '#999', fontSize: 12, padding: '8px 0' }}>
                            暂无关联角色
                          </div>
                        )
                      )}
                    </Spin>
                  </div>
                </>
              ) : (
                <Empty
                  description="请在左侧选择一个权限节点查看详情"
                  style={{ marginTop: 80 }}
                />
              )}
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Permission Form Drawer */}
      <PermissionForm
        visible={formVisible}
        permission={editingPermission}
        parentId={parentIdForNew}
        permissionTree={permissionTree}
        onClose={() => {
          setFormVisible(false);
          setEditingPermission(null);
          setParentIdForNew(undefined);
        }}
        onSuccess={handleFormSuccess}
      />

      {/* Permission Check Drawer */}
      <Drawer
        title="权限检查工具"
        placement="right"
        width={520}
        open={checkVisible}
        onClose={() => setCheckVisible(false)}
      >
        <Form
          form={checkForm}
          layout="vertical"
          onFinish={handlePermissionCheck}
        >
          <Form.Item
            label="用户 ID"
            name="userId"
            rules={[{ required: true, message: '请输入用户 ID' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="输入用户 ID" min={1} />
          </Form.Item>
          <Form.Item
            label="权限编码"
            name="permissionCodes"
            rules={[{ required: true, message: '请输入权限编码' }]}
            extra="多个编码用逗号、换行或空格分隔"
          >
            <Input.TextArea
              rows={4}
              placeholder="例如: user:list, role:create, menu:update"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<AimOutlined />}
              loading={checkLoading}
              block
            >
              执行检查
            </Button>
          </Form.Item>
        </Form>

        {checkResults && (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 12, fontWeight: 600 }}>
              检查结果（共 {Object.keys(checkResults).length} 项）
            </div>
            <List
              dataSource={Object.entries(checkResults)}
              renderItem={([code, result]) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      result.allowed ? (
                        <Badge status="success" />
                      ) : (
                        <Badge status="error" />
                      )
                    }
                    title={
                      <Space>
                        <code>{code}</code>
                        <Tag
                          icon={result.allowed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          color={result.allowed ? 'success' : 'error'}
                        >
                          {result.allowed ? '允许' : '拒绝'}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={2}>
                        <span>来源: {result.source}</span>
                        {result.reason && <span>原因: {result.reason}</span>}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default PermissionResourceList;
